import {inject, injectable} from 'inversify';

import type {BaseDomainFactory} from '../Base/BaseDomain.factory';

import {ModelsRegistry} from '../../system/modelsRegistry/ModelsRegistry';
import {DateTimeModelFactory} from '../Date/DateTime.factory';
import {TenantFactory} from '../Tenant/Tenant.factory';
import {SubtenantModel} from './Subtenant.model';
import {SubtenantArticlesAccessModelFactory} from './SubtenantArticlesAccess.factory';
import {SubtenantExtensionConfigurationFactory} from './SubtenantExtensionConfiguration.factory';

@injectable()
export class SubtenantFactory
  implements BaseDomainFactory<SubtenantModel, SubtenantModel.Props>
{
  constructor(
    @inject(DateTimeModelFactory)
    private readonly dateTimeModelFactory: DateTimeModelFactory,
    @inject(SubtenantArticlesAccessModelFactory)
    private readonly subtenantArticlesAccessModelFactory: SubtenantArticlesAccessModelFactory,
    @inject(SubtenantExtensionConfigurationFactory)
    private readonly subtenantExtensionConfigurationFactory: SubtenantExtensionConfigurationFactory,
    @inject(TenantFactory)
    private readonly tenantFactory: TenantFactory,
    @inject(ModelsRegistry)
    private readonly modelsRegistry: ModelsRegistry,
  ) {}

  create(props: SubtenantModel.Props): SubtenantModel {
    return this.modelsRegistry.put(new SubtenantModel(props));
  }

  createEmpty(): SubtenantModel {
    return new SubtenantModel({
      articlesAccess: this.subtenantArticlesAccessModelFactory.createEmpty(),
      coachIds: [],
      colors: {
        onboardingBg: '#ccc',
        primary: '#c0c',
      },
      created: this.dateTimeModelFactory.now(),
      email: {
        sender: {
          email: '',
          name: '',
        },
        signature: {
          client: '',
          coach: '',
        },
        templateFolder: '',
      },
      extensionConfiguration:
        this.subtenantExtensionConfigurationFactory.createEmpty(),
      features: {
        autoMatching: false,
        digitalTwin: false,
        library: false,
        trial: {
          days: 0,
          sessionsLimit: 0,
        },
        waitlist: false,
      },
      id: 'subtenant:not_found',
      limits: {
        conferenceBooking: 0,
        maxActiveUsers: 0,
        subgoalCreation: 0,
        taskCreation: 0,
      },
      partnerId: '',
      program: {
        duration: 0,
        title: 'Program not found',
      },
      session: {
        count: 0,
        duration: 0,
        extraLimit: 0,
        intersessionBreak: 0,
      },
      share: {
        access: {
          description: '',
          link: '',
        },
      },
      tenant: this.tenantFactory.createEmpty(),
      title: 'Subtenant not found',
      updated: this.dateTimeModelFactory.now(),
      urls: {
        faq: '',
        widget: '',
      },
      userReview: {
        secret: '',
        url: '',
      },
      widget: {
        confirmation: null,
        isAccessible: false,
        uxType: 'full',
      },
    });
  }
}
